// =============================================================================
// TOKENS DE SOMBRAS Y EFECTOS - SISTEMA DE DISEÑO TADMIN
// =============================================================================
// Tokens de sombras centralizados para crear jerarquía visual
// y efectos de profundidad consistentes

// Sombras base del sistema
export const baseShadows = {
  // Sin sombra
  none: 'none',
  
  // Sombras sutiles (elevación baja)
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  
  // Sombras estándar (elevación media)
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  
  // Sombras pronunciadas (elevación alta)
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  
  // Sombra interior
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
};

// Sombras coloreadas para estados específicos
export const coloredShadows = {
  // Sombras de enfoque (focus)
  focus: {
    primary: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    secondary: '0 0 0 3px rgba(99, 102, 241, 0.1)',
    success: '0 0 0 3px rgba(34, 197, 94, 0.1)',
    error: '0 0 0 3px rgba(239, 68, 68, 0.1)',
    warning: '0 0 0 3px rgba(245, 158, 11, 0.1)',
  },
  
  // Sombras de marca/branding
  brand: {
    primary: '0 4px 14px 0 rgba(59, 130, 246, 0.15)',
    secondary: '0 4px 14px 0 rgba(99, 102, 241, 0.15)',
  },
  
  // Sombras de estado
  state: {
    success: '0 4px 14px 0 rgba(34, 197, 94, 0.15)',
    error: '0 4px 14px 0 rgba(239, 68, 68, 0.15)',
    warning: '0 4px 14px 0 rgba(245, 158, 11, 0.15)',
    info: '0 4px 14px 0 rgba(59, 130, 246, 0.15)',
  },
};

// Sombras específicas para componentes
export const componentShadows = {
  // Botones
  button: {
    default: baseShadows.sm,
    hover: baseShadows.md,
    active: baseShadows.xs,
    focus: coloredShadows.focus.primary,
    disabled: baseShadows.none,
  },
  
  // Cards
  card: {
    default: baseShadows.sm,
    hover: baseShadows.lg,
    elevated: baseShadows.xl,
    interactive: baseShadows.md,
  },
  
  // Modales y overlays
  modal: {
    backdrop: '0 0 0 1px rgba(0, 0, 0, 0.05), 0 25px 50px -12px rgba(0, 0, 0, 0.4)',
    content: baseShadows['2xl'],
  },
  
  // Dropdowns y menús
  dropdown: {
    default: baseShadows.lg,
    large: baseShadows.xl,
  },
  
  // Tooltips
  tooltip: {
    default: baseShadows.md,
  },
  
  // Inputs y formularios
  input: {
    default: baseShadows.xs,
    focus: coloredShadows.focus.primary,
    error: coloredShadows.focus.error,
    success: coloredShadows.focus.success,
  },
  
  // Navegación
  nav: {
    header: baseShadows.sm,
    sidebar: baseShadows.lg,
    floating: baseShadows.xl,
  },
  
  // Tablas
  table: {
    header: baseShadows.xs,
    row: baseShadows.none,
    hover: baseShadows.xs,
  },
  
  // Notificaciones
  notification: {
    default: baseShadows.lg,
    success: coloredShadows.state.success,
    error: coloredShadows.state.error,
    warning: coloredShadows.state.warning,
    info: coloredShadows.state.info,
  },
  
  // Badges y chips
  badge: {
    default: baseShadows.xs,
    elevated: baseShadows.sm,
  },
};

// Sombras para diferentes niveles de elevación (Material Design inspired)
export const elevationShadows = {
  0: baseShadows.none,
  1: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
  2: '0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)',
  3: '0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)',
  4: '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
  5: '0 19px 38px rgba(0, 0, 0, 0.30), 0 15px 12px rgba(0, 0, 0, 0.22)',
};

// Sombras para tema oscuro
export const darkShadows = {
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.3)',
};

// Efectos adicionales
export const effects = {
  // Blur effects
  blur: {
    none: 'blur(0)',
    sm: 'blur(4px)',
    md: 'blur(8px)',
    lg: 'blur(16px)',
    xl: 'blur(24px)',
    '2xl': 'blur(40px)',
    '3xl': 'blur(64px)',
  },
  
  // Backdrop blur
  backdropBlur: {
    none: 'none',
    sm: 'blur(4px)',
    md: 'blur(8px)',
    lg: 'blur(16px)',
    xl: 'blur(24px)',
  },
  
  // Brightness effects
  brightness: {
    50: 'brightness(0.5)',
    75: 'brightness(0.75)',
    90: 'brightness(0.9)',
    95: 'brightness(0.95)',
    100: 'brightness(1)',
    105: 'brightness(1.05)',
    110: 'brightness(1.1)',
    125: 'brightness(1.25)',
    150: 'brightness(1.5)',
  },
  
  // Contrast effects
  contrast: {
    50: 'contrast(0.5)',
    75: 'contrast(0.75)',
    100: 'contrast(1)',
    125: 'contrast(1.25)',
    150: 'contrast(1.5)',
    200: 'contrast(2)',
  },
  
  // Grayscale effects
  grayscale: {
    0: 'grayscale(0)',
    25: 'grayscale(0.25)',
    50: 'grayscale(0.5)',
    75: 'grayscale(0.75)',
    100: 'grayscale(1)',
  },
  
  // Opacity effects
  opacity: {
    0: '0',
    5: '0.05',
    10: '0.1',
    20: '0.2',
    25: '0.25',
    30: '0.3',
    40: '0.4',
    50: '0.5',
    60: '0.6',
    70: '0.7',
    75: '0.75',
    80: '0.8',
    90: '0.9',
    95: '0.95',
    100: '1',
  },
};

// Sombras específicas para el sistema de pagos
export const paymentShadows = {
  // Cards de método de pago
  paymentCard: {
    default: baseShadows.sm,
    selected: coloredShadows.brand.primary,
    hover: baseShadows.md,
    error: coloredShadows.state.error,
  },
  
  // Formularios de pago
  paymentForm: {
    container: baseShadows.lg,
    field: baseShadows.xs,
    fieldFocus: coloredShadows.focus.primary,
    fieldError: coloredShadows.focus.error,
  },
  
  // Modales de confirmación
  paymentModal: {
    backdrop: componentShadows.modal.backdrop,
    content: componentShadows.modal.content,
  },
  
  // Estados de transacción
  transaction: {
    pending: '0 4px 14px 0 rgba(245, 158, 11, 0.15)',
    success: coloredShadows.state.success,
    failed: coloredShadows.state.error,
  },
};

// Utilidades para crear sombras personalizadas
export const shadowUtilities = {
  // Crear sombra con color personalizado
  createColoredShadow: (color, opacity = 0.15, blur = 14) => {
    return `0 4px ${blur}px 0 ${color.replace(')', `, ${opacity})`).replace('rgb', 'rgba')}`;
  },
  
  // Crear sombra de elevación
  createElevationShadow: (level) => {
    const shadows = {
      1: '0 1px 3px rgba(0, 0, 0, 0.12)',
      2: '0 3px 6px rgba(0, 0, 0, 0.16)',
      3: '0 10px 20px rgba(0, 0, 0, 0.19)',
      4: '0 14px 28px rgba(0, 0, 0, 0.25)',
      5: '0 19px 38px rgba(0, 0, 0, 0.30)',
    };
    return shadows[level] || shadows[1];
  },
  
  // Combinar múltiples sombras
  combineShadows: (...shadows) => {
    return shadows.filter(Boolean).join(', ');
  },
  
  // Crear sombra responsiva
  createResponsiveShadow: (mobile, desktop) => {
    return {
      boxShadow: mobile,
      '@media (min-width: 768px)': {
        boxShadow: desktop,
      },
    };
  },
};

// Animaciones de sombra
export const shadowAnimations = {
  // Transiciones suaves
  transition: {
    fast: 'box-shadow 0.15s ease-in-out',
    normal: 'box-shadow 0.2s ease-in-out',
    slow: 'box-shadow 0.3s ease-in-out',
  },
  
  // Efectos hover
  hover: {
    lift: {
      default: baseShadows.sm,
      hover: baseShadows.lg,
      transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
    },
    glow: {
      default: baseShadows.xs,
      hover: coloredShadows.brand.primary,
      transition: 'box-shadow 0.3s ease-in-out',
    },
  },
};

// Exportar todo
const shadowTokens = {
  base: baseShadows,
  colored: coloredShadows,
  component: componentShadows,
  elevation: elevationShadows,
  dark: darkShadows,
  effects,
  payment: paymentShadows,
  utilities: shadowUtilities,
  animations: shadowAnimations,
};
export default shadowTokens;